import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  LogOut, User, MapPin, Home, Languages, Wallet, Edit3, Save, X,
  Loader2, ClipboardList, Home as HomeIcon, Youtube, HeartHandshake,
  Crown, ShieldCheck, UserPlus, KeyRound, Trash2,
} from "lucide-react";
import ElderraBookingModal from "@/components/ElderraBookingModal";
import OrderFeed from "@/components/caregiver/OrderFeed";
import CaregiverChat from "@/components/caregiver/CaregiverChat";
import { StatusStrip, ActionLogCard, ConsentLogCard, SetPinBanner, BillSettingsCard, PendingApprovalsCard } from "@/components/caregiver/CaregiverCards";
import { PaymentsCard } from "@/components/caregiver/PaymentsCard";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
// The auth cookie is sent automatically (axios withCredentials).
const authHeaders = () => ({});

const LANGUAGES = { en: "English", hi: "Hindi", bn: "Bengali", ta: "Tamil", te: "Telugu", mr: "Marathi", gu: "Gujarati", kn: "Kannada", ml: "Malayalam", pa: "Punjabi" };

function SeniorProfileCard({ senior, onSaved }) {
  const [editing, setEditing] = useState(false);
  const [city, setCity] = useState(senior.city || "");
  const [address, setAddress] = useState(senior.address || "");
  const [language, setLanguage] = useState(senior.language || "en");
  const [paymentMode, setPaymentMode] = useState(senior.payment_mode || "cash");
  const [saving, setSaving] = useState(false);
  const inputCls = "w-full min-h-[52px] px-3.5 text-base border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766] bg-white";

  const save = async () => {
    setSaving(true);
    try {
      const { data } = await axios.patch(`${API}/family/senior-profile`, { city, address, language, payment_mode: paymentMode }, authHeaders());
      onSaved(data.profile);
      setEditing(false);
      toast.success(`${senior.name}'s profile updated`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not save changes.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="senior-profile-card">
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-12 h-12 rounded-full bg-[#2D2766] flex items-center justify-center text-white font-bold text-lg">
            {(senior.name || "?").charAt(0).toUpperCase()}
          </div>
          <div>
            <p className="text-lg font-semibold text-[#1A1A1A]" data-testid="senior-name">{senior.name}</p>
            <p className="text-sm text-gray-500">{senior.phone}</p>
          </div>
        </div>
        {!editing && (
          <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-sm font-medium text-[#2D2766] hover:underline" data-testid="senior-edit-btn">
            <Edit3 className="w-4 h-4" /> Edit
          </button>
        )}
      </div>

      {!editing ? (
        <div className="space-y-2.5 text-base text-[#333333]">
          <p className="flex items-center gap-2"><MapPin className="w-4 h-4 text-gray-400" /> {senior.city || "—"}</p>
          <p className="flex items-center gap-2"><Home className="w-4 h-4 text-gray-400" /> {senior.address || "—"}</p>
          <p className="flex items-center gap-2"><Languages className="w-4 h-4 text-gray-400" /> {LANGUAGES[senior.language] || senior.language}</p>
          <p className="flex items-center gap-2 capitalize"><Wallet className="w-4 h-4 text-gray-400" /> {senior.payment_mode}</p>
        </div>
      ) : (
        <div className="space-y-3.5" data-testid="senior-edit-form">
          <div>
            <label className="text-sm font-semibold text-gray-600 mb-1.5 block">City</label>
            <input className={inputCls} value={city} onChange={(e) => setCity(e.target.value)} data-testid="senior-edit-city" />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600 mb-1.5 block">Address</label>
            <input className={inputCls} value={address} onChange={(e) => setAddress(e.target.value)} data-testid="senior-edit-address" />
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600 mb-1.5 block">Language</label>
            <select className={inputCls} value={language} onChange={(e) => setLanguage(e.target.value)} data-testid="senior-edit-language">
              {Object.entries(LANGUAGES).map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </div>
          <div>
            <label className="text-sm font-semibold text-gray-600 mb-1.5 block">Payment mode</label>
            <div className="flex gap-2">
              {["cash", "online"].map((m) => (
                <button key={m} type="button" onClick={() => setPaymentMode(m)}
                  className="flex-1 min-h-[44px] rounded-xl border-2 font-medium capitalize text-sm transition-all"
                  style={paymentMode === m ? { borderColor: "#2D2766", backgroundColor: "#2D276614", color: "#2D2766" } : { borderColor: "#D1D5DB", color: "#333333" }}
                  data-testid={`senior-edit-payment-${m}`}
                >{m}</button>
              ))}
            </div>
          </div>
          <div className="flex gap-2 pt-1">
            <button onClick={save} disabled={saving} className="flex-1 min-h-[48px] bg-[#2D2766] text-white rounded-xl font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="senior-edit-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />} Save
            </button>
            <button onClick={() => setEditing(false)} className="px-4 min-h-[48px] border-2 border-gray-300 rounded-xl text-sm font-medium text-gray-600" data-testid="senior-edit-cancel-btn">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CaregiversCard({ seniorName }) {
  const [caregivers, setCaregivers] = useState([]);
  const [hasPrimary, setHasPrimary] = useState(false);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [newPin, setNewPin] = useState("");
  const [showAdd, setShowAdd] = useState(false);
  const [addName, setAddName] = useState("");
  const [addPhone, setAddPhone] = useState("");
  const [addPin, setAddPin] = useState("");

  const iAmPrimary = caregivers.some((c) => c.is_self && c.is_primary);
  const inputCls = "w-full min-h-[48px] px-3.5 text-base border-2 border-gray-300 rounded-xl focus:outline-none focus:ring-4 focus:ring-[#2D2766]/20 focus:border-[#2D2766] bg-white";

  const load = async () => {
    try {
      const { data } = await axios.get(`${API}/family/caregivers`, authHeaders());
      setCaregivers(data.caregivers || []);
      setHasPrimary(!!data.has_primary);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load caregivers.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const becomePrimary = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/family/become-primary`, {}, authHeaders());
      toast.success("You are now the primary caregiver");
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not update.");
    } finally {
      setBusy(false);
    }
  };

  const resetSeniorPin = async () => {
    if (!/^\d{4}$/.test(newPin)) { toast.error("Enter a 4-digit PIN."); return; }
    setBusy(true);
    try {
      await axios.post(`${API}/family/senior-pin`, { new_pin: newPin }, authHeaders());
      toast.success(`${seniorName}'s login PIN was reset`);
      setNewPin("");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not reset PIN.");
    } finally {
      setBusy(false);
    }
  };

  const addCaregiver = async () => {
    if (addName.trim().length < 2 || !addPhone.trim() || !/^\d{4}$/.test(addPin)) {
      toast.error("Enter name, phone, and a 4-digit PIN."); return;
    }
    setBusy(true);
    try {
      await axios.post(`${API}/family/add-caregiver`, { name: addName, phone: addPhone, pin: addPin }, authHeaders());
      toast.success(`${addName} can now log in as a caregiver`);
      setAddName(""); setAddPhone(""); setAddPin(""); setShowAdd(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not add caregiver.");
    } finally {
      setBusy(false);
    }
  };

  const removeCaregiver = async (phone, name) => {
    if (!window.confirm(`Remove ${name} as a caregiver? They will lose access immediately.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/family/caregiver/${encodeURIComponent(phone)}`, authHeaders());
      toast.success(`${name} removed`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not remove caregiver.");
    } finally {
      setBusy(false);
    }
  };

  const makePrimary = async (phone, name) => {
    if (!window.confirm(`Make ${name} the primary caregiver? You will lose the admin controls.`)) return;
    setBusy(true);
    try {
      await axios.post(`${API}/family/make-primary/${encodeURIComponent(phone)}`, {}, authHeaders());
      toast.success(`${name} is now the primary caregiver`);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not transfer primary caregiver.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 p-5" data-testid="caregivers-card">
      <div className="flex items-center justify-between mb-3">
        <p className="font-semibold text-[#1A1A1A] flex items-center gap-2">
          <ShieldCheck className="w-5 h-5 text-[#2D2766]" /> Caregivers
        </p>
        {iAmPrimary && (
          <span className="flex items-center gap-1 text-xs font-semibold text-amber-700 bg-amber-50 px-2.5 py-1 rounded-full" data-testid="you-are-primary-badge">
            <Crown className="w-3.5 h-3.5" /> You are primary
          </span>
        )}
      </div>

      {loading ? (
        <div className="flex justify-center py-4"><Loader2 className="w-5 h-5 animate-spin text-[#2D2766]" /></div>
      ) : (
        <>
          <div className="space-y-2" data-testid="caregivers-list">
            {caregivers.map((c) => (
              <div key={c.phone} className="flex items-center justify-between rounded-xl border border-gray-100 px-3 py-2.5" data-testid={`caregiver-${c.phone}`}>
                <div className="flex items-center gap-2.5 min-w-0">
                  <div className="w-9 h-9 rounded-full bg-[#3D3580] flex items-center justify-center text-white text-sm font-bold shrink-0">
                    {(c.name || "?").charAt(0).toUpperCase()}
                  </div>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-[#1A1A1A] truncate">
                      {c.name} {c.is_self && <span className="text-gray-400">(you)</span>}
                    </p>
                    <p className="text-xs text-gray-400 truncate">{c.phone}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  {c.is_primary && (
                    <span className="flex items-center gap-1 text-xs font-semibold text-amber-700" data-testid={`primary-badge-${c.phone}`}>
                      <Crown className="w-3.5 h-3.5" /> Primary
                    </span>
                  )}
                  {iAmPrimary && !c.is_self && !c.is_primary && (
                    <button onClick={() => makePrimary(c.phone, c.name)} disabled={busy} className="text-xs font-medium text-[#2D2766] hover:underline disabled:opacity-50" data-testid={`make-primary-${c.phone}`}>
                      Make primary
                    </button>
                  )}
                  {iAmPrimary && !c.is_self && (
                    <button onClick={() => removeCaregiver(c.phone, c.name)} disabled={busy} className="p-1.5 text-gray-400 hover:text-red-500 disabled:opacity-50" data-testid={`remove-caregiver-${c.phone}`}>
                      <Trash2 className="w-4 h-4" />
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>

          {!iAmPrimary && !hasPrimary && (
            <button onClick={becomePrimary} disabled={busy} className="mt-3 w-full min-h-[48px] rounded-xl bg-[#2D2766] text-white font-semibold text-sm flex items-center justify-center gap-2 disabled:opacity-60" data-testid="become-primary-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Crown className="w-4 h-4" />} Become Primary Caregiver
            </button>
          )}

          {iAmPrimary && (
            <div className="mt-4 pt-4 border-t space-y-4" data-testid="primary-admin-controls">
              <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Admin controls</p>

              <div>
                <label className="text-sm font-semibold text-gray-600 mb-1.5 flex items-center gap-1.5"><KeyRound className="w-4 h-4" /> Reset {seniorName}'s login PIN</label>
                <div className="flex gap-2">
                  <input inputMode="numeric" maxLength={4} value={newPin}
                    onChange={(e) => setNewPin(e.target.value.replace(/\D/g, "").slice(0, 4))}
                    placeholder="New 4-digit PIN" className={inputCls} data-testid="senior-pin-input" />
                  <button onClick={resetSeniorPin} disabled={busy} className="px-4 min-h-[48px] bg-[#2D2766] text-white rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="senior-pin-save-btn">
                    Reset
                  </button>
                </div>
              </div>

              {showAdd ? (
                <div className="space-y-2.5 p-3 bg-indigo-50 rounded-xl" data-testid="add-caregiver-form">
                  <input value={addName} onChange={(e) => setAddName(e.target.value)} placeholder="Caregiver name" className={inputCls} data-testid="add-caregiver-name" />
                  <input value={addPhone} onChange={(e) => setAddPhone(e.target.value)} placeholder="Phone number" className={inputCls} data-testid="add-caregiver-phone" />
                  <input inputMode="numeric" maxLength={4} value={addPin} onChange={(e) => setAddPin(e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="4-digit login PIN" className={inputCls} data-testid="add-caregiver-pin" />
                  <div className="flex gap-2">
                    <button onClick={addCaregiver} disabled={busy} className="flex-1 min-h-[44px] bg-[#2D2766] text-white rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="add-caregiver-save">Add caregiver</button>
                    <button onClick={() => setShowAdd(false)} className="px-4 min-h-[44px] border-2 border-gray-300 rounded-xl text-sm text-gray-600">Cancel</button>
                  </div>
                </div>
              ) : (
                <button onClick={() => setShowAdd(true)} className="flex items-center gap-1.5 text-sm font-semibold text-[#2D2766] hover:underline" data-testid="add-caregiver-btn">
                  <UserPlus className="w-4 h-4" /> Add another caregiver
                </button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}


export default function FamilyDashboard({ profile, onLogout }) {
  const [senior, setSenior] = useState(null);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [bookingKind, setBookingKind] = useState(null);
  const [needsPin, setNeedsPin] = useState(!!profile.must_change_pin);

  const load = async () => {
    try {
      const [sRes, oRes] = await Promise.all([
        axios.get(`${API}/family/senior-profile`, authHeaders()),
        axios.get(`${API}/family/senior-feed`, authHeaders()),
      ]);
      setSenior(sRes.data.profile);
      setOrders(oRes.data.orders || []);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Could not load the caregiver dashboard.");
    } finally {
      setLoading(false);
      setOrdersLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const firstName = (senior?.name || "").split(" ")[0] || "them";

  return (
    <div className="flex flex-col h-full bg-[#F7F6FB]" data-testid="family-dashboard">
      <header className="flex items-center justify-between px-4 py-4 md:px-6 shrink-0" style={{ backgroundColor: "#2D2766" }} data-testid="family-dashboard-header">
        <div className="flex items-center gap-2.5">
          <HeartHandshake className="w-6 h-6 text-white" />
          <div>
            <h1 className="text-white font-semibold text-lg leading-tight">Caregiver Dashboard</h1>
            <p className="text-purple-200 text-xs" data-testid="caregiver-context-line">
              {senior ? `${senior.name}'s account · you are ${profile.name}` : `Logged in as ${profile.name}`}
            </p>
          </div>
        </div>
        <button onClick={onLogout} className="flex items-center gap-1.5 px-3 py-2 rounded-full bg-white/10 hover:bg-white/20 text-white text-sm font-medium transition-colors" data-testid="family-logout-btn">
          <LogOut className="w-4 h-4" /> Log out
        </button>
      </header>

      <div className="flex-1 overflow-y-auto px-4 py-5 md:px-6">
        {loading ? (
          <div className="flex items-center justify-center h-40"><Loader2 className="w-6 h-6 animate-spin text-[#2D2766]" /></div>
        ) : !senior ? (
          <p className="text-center text-gray-400 mt-10" data-testid="family-dashboard-no-senior">Could not find the linked senior's profile.</p>
        ) : (
          <div className="max-w-2xl mx-auto space-y-6">
            {needsPin && <SetPinBanner onDone={() => setNeedsPin(false)} />}

            <StatusStrip orders={orders} seniorName={firstName} />

            <div>
              <p className="text-sm font-semibold text-gray-500 mb-2.5 flex items-center gap-1.5">
                <ClipboardList className="w-4 h-4" /> {senior.name}'s orders
              </p>
              <OrderFeed orders={orders} loading={ordersLoading} seniorName={firstName} />
            </div>

            <CaregiverChat seniorName={firstName} />

            <SeniorProfileCard senior={senior} onSaved={setSenior} />

            <CaregiversCard seniorName={senior.name} />

            <ActionLogCard />

            <ConsentLogCard />

            {profile.is_primary_caregiver && <BillSettingsCard />}

            <PendingApprovalsCard />

            <PaymentsCard />

            <div>
              <p className="text-sm font-semibold text-gray-500 mb-2.5 flex items-center gap-1.5"><User className="w-4 h-4" /> Book a service for {firstName}</p>
              <div className="grid grid-cols-2 gap-3">
                <button onClick={() => setBookingKind("digimitra")} className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-[#4F46E5] transition-colors" data-testid="family-book-digimitra">
                  <HomeIcon className="w-6 h-6 text-[#4F46E5]" />
                  <span className="text-sm font-medium text-[#1A1A1A]">DigiMitra Visit</span>
                </button>
                <button onClick={() => setBookingKind("youtuber")} className="bg-white border border-gray-200 rounded-2xl p-4 flex flex-col items-center gap-2 hover:border-[#E11D48] transition-colors" data-testid="family-book-youtuber">
                  <Youtube className="w-6 h-6 text-[#E11D48]" />
                  <span className="text-sm font-medium text-[#1A1A1A]">YouTuber Program</span>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {bookingKind && senior && (
        <ElderraBookingModal
          kind={bookingKind}
          sessionId={`family-${senior.phone}`}
          prefillProfile={senior}
          onClose={() => setBookingKind(null)}
        />
      )}
    </div>
  );
}
